import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollText,
  ChevronDown,
  ChevronUp,
  Clock,
  Volume2,
  CigaretteOff,
  PawPrint,
  PartyPopper,
  Trash2
} from 'lucide-react';

interface HouseRulesCardProps {
  rules: string | null;
  darkMode: boolean;
  checkInTime?: string | null;
  checkOutTime?: string | null;
}

// Ikone za prepoznate ključne riječi (HR + EN + DE + IT + SL)
const ruleIcons: { [key: string]: React.ReactNode } = {
  // Smoking
  'puš': <CigaretteOff className="w-4 h-4" />,
  'smok': <CigaretteOff className="w-4 h-4" />,
  'cigaret': <CigaretteOff className="w-4 h-4" />,
  'rauch': <CigaretteOff className="w-4 h-4" />,
  'fumar': <CigaretteOff className="w-4 h-4" />,
  // Pets
  'ljubim': <PawPrint className="w-4 h-4" />,
  'životinj': <PawPrint className="w-4 h-4" />,
  'kućn': <PawPrint className="w-4 h-4" />,
  'pet': <PawPrint className="w-4 h-4" />,
  'animal': <PawPrint className="w-4 h-4" />,
  'tier': <PawPrint className="w-4 h-4" />,
  'haustier': <PawPrint className="w-4 h-4" />,
  // Quiet hours
  'tišin': <Volume2 className="w-4 h-4" />,
  'mir': <Volume2 className="w-4 h-4" />,
  'buk': <Volume2 className="w-4 h-4" />,
  'quiet': <Volume2 className="w-4 h-4" />,
  'noise': <Volume2 className="w-4 h-4" />,
  'silent': <Volume2 className="w-4 h-4" />,
  'ruhe': <Volume2 className="w-4 h-4" />,
  // Parties
  'parti': <PartyPopper className="w-4 h-4" />,
  'party': <PartyPopper className="w-4 h-4" />,
  'zabav': <PartyPopper className="w-4 h-4" />,
  'slavlj': <PartyPopper className="w-4 h-4" />,
  'fest': <PartyPopper className="w-4 h-4" />,
  // Trash
  'smeć': <Trash2 className="w-4 h-4" />,
  'otpad': <Trash2 className="w-4 h-4" />,
  'reciklir': <Trash2 className="w-4 h-4" />,
  'trash': <Trash2 className="w-4 h-4" />,
  'garbage': <Trash2 className="w-4 h-4" />,
  'müll': <Trash2 className="w-4 h-4" />,
};

const getIconForRule = (rule: string): React.ReactNode => {
  const lowerRule = rule.toLowerCase();
  for (const [keyword, icon] of Object.entries(ruleIcons)) {
    if (lowerRule.includes(keyword)) {
      return icon;
    }
  }
  return <ScrollText className="w-4 h-4" />;
};

// Helper to format time from "HH:MM:SS" or "HH:MM" to "HH:MM"
const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  // Handle "HH:MM:SS" or "HH:MM" format
  const parts = time.split(':');
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return time;
};

/**
 * Split the host's house-rules text into individual rules.
 * The setup wizard writes ONE RULE PER LINE, so newlines are the primary
 * separator. Splitting on every "." broke times like "22.00–07.00" into
 * "22", "00–07", "00". Only a single-paragraph text falls back to sentence
 * boundaries — a period/!/? followed by a space and a capital letter.
 */
function splitHouseRules(text: string): string[] {
  const clean = (r: string) => r.replace(/^\s*(?:[-•*·]|\d+[.)])\s+/, '').trim();
  const lines = text.split(/\r?\n/).map(clean).filter((r) => r.length > 0);
  if (lines.length !== 1) return lines;
  const sentences = lines[0]
    .replace(/([.!?])\s+(?=\p{Lu})/gu, '$1\n')
    .split('\n')
    .map(clean)
    .filter((r) => r.length > 0);
  return sentences.length > 0 ? sentences : lines;
}

export const HouseRulesCard = memo(function HouseRulesCard({ rules, darkMode, checkInTime, checkOutTime }: HouseRulesCardProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Only times the host actually set — never invented 14:00 / 10:00 defaults.
  const formattedCheckIn = formatTime(checkInTime);
  const formattedCheckOut = formatTime(checkOutTime);

  if (!rules || !rules.trim()) return null;

  const rulesList = splitHouseRules(rules);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-gradient-to-br from-white to-rose-50/30 border border-rose-100 shadow-xl shadow-gray-200/80'
      }`}
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />

      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`relative w-full p-5 flex items-center justify-between transition-colors ${
          darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-rose-50/50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="text-left">
            <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.houseRules.title', 'Kućni red')}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {/* Plural-aware word ("1 rule" / "1 pravilo", "3 pravila") */}
              {rulesList.length} {t('guest.houseRules.ruleWord', 'pravila', { count: rulesList.length })}
            </p>
          </div>
        </div>
        <div className={`p-2 rounded-xl transition-all ${
          isExpanded
            ? darkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-600'
            : darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-400'
        }`}>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className={`px-5 pb-5 ${darkMode ? 'border-t border-slate-700/50' : 'border-t border-rose-100'}`}>
          <div className="mt-4 space-y-2">
            {rulesList.map((rule, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 p-3 rounded-xl transition-all ${
                  darkMode
                    ? 'bg-slate-700/30 hover:bg-slate-700/50'
                    : 'bg-rose-50/50 hover:bg-rose-50'
                }`}
              >
                <div className={`p-2 rounded-lg shrink-0 ${
                  darkMode
                    ? 'bg-rose-500/20 text-rose-400'
                    : 'bg-rose-100 text-rose-600'
                }`}>
                  {getIconForRule(rule)}
                </div>
                <p className={`text-sm leading-relaxed pt-1.5 ${
                  darkMode ? 'text-slate-200' : 'text-gray-700'
                }`}>
                  {rule}
                </p>
              </div>
            ))}
          </div>

          {/* Check-in/out times */}
          {(formattedCheckIn || formattedCheckOut) && (
          <div className={`mt-4 p-4 rounded-xl ${
            darkMode
              ? 'bg-gradient-to-r from-slate-700/50 to-slate-700/30 border border-slate-600/50'
              : 'bg-gradient-to-r from-gray-50 to-white border border-gray-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Clock className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
              <span className={`text-xs font-semibold uppercase tracking-wider ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}>
                {t('guest.houseRules.times', 'Vrijeme dolaska i odlaska')}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {formattedCheckIn && (
                <div>
                  <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>{t('guest.houseRules.checkIn', 'Check-in')}</p>
                  <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('guest.houseRules.from', 'od')} {formattedCheckIn}</p>
                </div>
              )}
              {formattedCheckOut && (
                <div>
                  <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>{t('guest.houseRules.checkOut', 'Check-out')}</p>
                  <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('guest.houseRules.until', 'do')} {formattedCheckOut}</p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default HouseRulesCard;
